import React, { useContext, useEffect, useReducer } from 'react';
import { wordingsReducer } from './wordingsReducer';
import {
	IDeleteWordingData,
	TTypeOfModalWindow,
	TWordingsSelectedItem,
	WordingsContext,
	wordingsInitialState,
} from './wordingsContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getLanguages } from 'backend/additionalInformation';
import { getAllWordingsInfo, deleteWording } from 'backend/adminSettings';
import { WordingsActionsTypes } from './wordingsActions';
import { IGetLanguages, IGetAllWordingsInfo, IUpdateWordings, IWordingsGroup } from 'types';

const WordingsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(wordingsReducer, wordingsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const { response, loading } = useFetchData<IGetAllWordingsInfo, null>(getAllWordingsInfo);
	const { response: languagesResponse } = useFetchData<IGetLanguages, null>(getLanguages);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: WordingsActionsTypes.SET_ALL_DATA,
				payload: {
					groups: response.data.groups,
				},
			});
		}
	}, [response]);

	useEffect(() => {
		if (languagesResponse) {
			dispatch({
				type: WordingsActionsTypes.SET_AVAILABLE_LANGUAGES,
				payload: {
					availableLanguages: languagesResponse.data.languages,
				},
			});
		}
	}, [languagesResponse]);

	const setGroup = (value: TWordingsSelectedItem | null) => {
		dispatch({
			type: WordingsActionsTypes.SET_GROUP,
			payload: {
				selectedGroup: value,
			},
		});
	};

	const setLanguage = (value: TWordingsSelectedItem | null) => {
		dispatch({
			type: WordingsActionsTypes.SET_LANGUAGE,
			payload: {
				selectedLanguage: value,
			},
		});
	};

	const addInvalidField = (value: string) => {
		dispatch({
			type: WordingsActionsTypes.ADD_INVALID_FIELD,
			payload: {
				invalidField: value,
			},
		});
	};

	const clearInvalidFields = () => {
		dispatch({
			type: WordingsActionsTypes.CLEAR_INVALID_FIELDS,
		});
	};

	const removeWording = async (groupId: string, wordingId: string) => {
		try {
			showLoader();
			await deleteWording(wordingId, 'General');

			const updatedWordings: IWordingsGroup[] = state.groups.map((group) =>
				group.id === groupId ? { ...group, items: group.items.filter((item) => item.id !== wordingId) } : group,
			);

			dispatch({
				type: WordingsActionsTypes.REMOVE_WORDING,
				payload: {
					groups: updatedWordings,
				},
			});
			dispatch({
				type: WordingsActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: WordingsActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const setDataForSubmit = (value: IUpdateWordings | null) => {
		dispatch({
			type: WordingsActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	};

	const setDeleteWordingData = (value: IDeleteWordingData | null) => {
		dispatch({
			type: WordingsActionsTypes.SET_DELETE_WORDING_DATA,
			payload: {
				deleteWordingData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: WordingsActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	return (
		<WordingsContext.Provider
			value={{
				setGroup,
				setLanguage,
				addInvalidField,
				clearInvalidFields,
				removeWording,
				setDataForSubmit,
				setDeleteWordingData,
				changeTypeOfModalWindow,
				selectedGroup: state.selectedGroup,
				selectedLanguage: state.selectedLanguage,
				availableLanguages: state.availableLanguages,
				groups: state.groups,
				invalidFields: state.invalidFields,
				dataForSubmit: state.dataForSubmit,
				deleteWordingData: state.deleteWordingData,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</WordingsContext.Provider>
	);
};

export default WordingsState;
